import type { KitchenArrivalRow, ShotNumberOverlay } from "./RallyBoardPopup";
import type { PounceWindowRow } from "../../shared/types/pounceEtc";
import type { CvJson, CvFrame, CvShotBallEvent } from "../../shared/types/cvJson";

export type PlayerId = 0 | 1 | 2 | 3;
export type Team01 = 0 | 1;

export type CausalityOverlayKind =
  | "setup"
  | "shift"
  | "no_return"
  | "finish"
  | "warning";

export type CausalityEvidenceSource =
  | "insights"
  | "cv"
  | "pounce"
  | "derived";

export type CausalityEvidencePolarity = "credit" | "blame" | "neutral";

export type CausalityEvidenceKind =
  | "shot_phase"
  | "shot_quality"
  | "ball_attackability"
  | "pounce_window"
  | "missed_attack"
  | "advantage_shift"
  | "terminal_outcome"
  | "pressure";

export type RallyCausalityEvidence = {
  kind: CausalityEvidenceKind;
  label: string;
  detail: string;
  weight: number;
  source: CausalityEvidenceSource;
  polarity: CausalityEvidencePolarity;
};

export type RallyCausalityShotRow = {
  rallyIndex: number;
  shotNumber: number;
  hitterPlayerId: PlayerId | null;
  hitterTeam: Team01 | null;

  shotType: string | null;
  quality: number | null;
  isFinalShot: boolean;

  storyKinds: string[];
  popupCreated: boolean;
  popupExploited: boolean;
  createdAttackWindow: boolean;
  tookPounce: boolean;
  missedPounce: boolean;

  advantageBefore: number | null;
  advantageAfter: number | null;
  advantageDeltaForHitterTeam: number | null;
  advantageDeltaAgainstHitterTeam: number | null;

  inevitabilityScore: number;
  swingScore: number;
  confidence: number;
  causalScore: number;
  phaseLabel: string;

  leverageTags: string[];
  evidence: RallyCausalityEvidence[];
  explanationShort: string;
  explanationLong?: string;
  overlayKind: CausalityOverlayKind | null;
};

export type RallyCausalitySummary = {
  rallyIndex: number;
  numShots: number;
  winningTeam: Team01 | null;
  pointAwarded: boolean | null;

  shotRows: RallyCausalityShotRow[];

  largestSwingShotNumber: number | null;
  setupShotNumber: number | null;
  noReturnShotNumber: number | null;
  finishShotNumber: number | null;

  decisivePlayerId: PlayerId | null;
  decisiveTeam: Team01 | null;

  summaryShort: string;
  summaryLong: string;
};

function isPopupCreated(ov: ShotNumberOverlay | null | undefined): boolean {
  return getPopupCreated(ov);
  //    (
  //     hasAnyKind(ov, [
  //       "popup",
  //       "popup_created",
  //       "popup:created",
  //       "popup_allowed",
  //       "attackable_popup",
  //     ]) ||
  //     (ov as any)?.popupCreated === true
  //   );
}

function getPopupCreated(ov: ShotNumberOverlay | null | undefined): boolean {
  const popup = (ov as any)?.rawShot?.errors?.popup;
  return popup === "potential";
}

function isPlayerId(v: unknown): v is PlayerId {
  return v === 0 || v === 1 || v === 2 || v === 3;
}

function isTeam01(v: unknown): v is Team01 {
  return v === 0 || v === 1;
}

function teamOfPlayerId(pid: unknown): Team01 | null {
  if (pid === 0 || pid === 1) return 0;
  if (pid === 2 || pid === 3) return 1;
  return null;
}

function otherTeam(team: Team01): Team01 {
  return team === 0 ? 1 : 0;
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function addEvidence(
  out: RallyCausalityEvidence[],
  evidence: RallyCausalityEvidence,
): void {
  out.push({
    ...evidence,
    weight: round2(evidence.weight),
  });
}

function getWinnerTeam(row: KitchenArrivalRow | null): Team01 | null {
  const v = row?.winnerTeam;
  return isTeam01(v) ? v : null;
}

function getPointAwarded(row: KitchenArrivalRow | null): boolean | null {
  return typeof row?.scoredPoint === "boolean" ? row.scoredPoint : null;
}

function getNumShots(row: KitchenArrivalRow | null): number {
  if (typeof row?.numShots === "number" && Number.isFinite(row.numShots)) {
    return row.numShots;
  }
  if (Array.isArray(row?.shotNumberOverlays)) {
    return row.shotNumberOverlays.length;
  }
  return 0;
}

function getOverlays(row: KitchenArrivalRow | null): ShotNumberOverlay[] {
  return Array.isArray(row?.shotNumberOverlays)
    ? (row!.shotNumberOverlays as ShotNumberOverlay[])
    : [];
}

function findOverlayByShotNumber(
  row: KitchenArrivalRow | null,
  shotNumber: number,
): ShotNumberOverlay | null {
  const overlays = getOverlays(row);
  for (const ov of overlays) {
    if (Number(ov?.shotNumber) === shotNumber) return ov;
  }
  return null;
}

function normalizeKind(s: string): string {
  return String(s).trim().toLowerCase().replace(/\s+/g, "_");
}

function storyKindSet(ov: ShotNumberOverlay | null | undefined): Set<string> {
  const out = new Set<string>();
  if (!ov) return out;

  for (const k of ov.storyKinds ?? []) {
    out.add(normalizeKind(k));
  }

  const ks = (ov as any)?.story?.kinds;
  if (Array.isArray(ks)) {
    for (const k of ks) out.add(normalizeKind(k));
  }

  return out;
}

function hasAnyKind(
  ov: ShotNumberOverlay | null | undefined,
  aliases: string[],
): boolean {
  const kinds = storyKindSet(ov);
  return aliases.some((a) => kinds.has(normalizeKind(a)));
}

function getShotType(ov: ShotNumberOverlay | null | undefined): string | null {
  if (!ov) return null;

  const candidates = [
    (ov as any)?.shotType,
    (ov as any)?.shot_type,
    (ov as any)?.rawShot?.shot_type,
    (ov as any)?.rawShot?.shotType,
    (ov as any)?.label,
    (ov as any)?.text,
  ];

  for (const c of candidates) {
    if (typeof c === "string" && c.trim()) {
      return c.trim();
    }
  }

  return null;
}

function normalizeShotTypeLabel(shotType: string | null): string {
  if (!shotType) return "shot";
  return shotType.replace(/_/g, " ").trim().toLowerCase() || "shot";
}

function getPhaseLabel(args: {
  shotNumber: number;
  shotType: string | null;
  isFinalShot: boolean;
}): string {
  const { shotNumber, shotType, isFinalShot } = args;
  const normalizedType = normalizeShotTypeLabel(shotType);

  if (shotNumber === 1) return "Serve";
  if (shotNumber === 2) return "Return";
  if (shotNumber === 3) return normalizedType.includes("drop") ? "3rd drop" : "3rd shot";
  if (shotNumber === 4) return "4th shot";
  if (shotNumber === 5) return "5th shot";
  if (normalizedType.includes("dink")) return "Dink pattern";
  if (normalizedType.includes("reset")) return "Reset";
  if (normalizedType.includes("drive")) return "Drive";
  if (isFinalShot) return "Finish";
  return "Rally build";
}

function getQuality(ov: ShotNumberOverlay | null | undefined): number | null {
  const q = (ov as any)?.rawShot?.quality?.overall;
  return typeof q === "number" && Number.isFinite(q) ? q : null;
}

function getAdvantageScaleArray(
  ov: ShotNumberOverlay | null | undefined,
): number[] | null {
  const candidates = [
    (ov as any)?.advantage_scale,
    (ov as any)?.advantageScale,
    (ov as any)?.rawShot?.advantage_scale,
    (ov as any)?.rawShot?.advantageScale,
  ];

  for (const c of candidates) {
    if (Array.isArray(c) && c.length >= 4) {
      const nums = c
        .slice(0, 4)
        .map((v: any) =>
          typeof v === "number" && Number.isFinite(v) ? v : null,
        );
      if (nums.every((v: any) => typeof v === "number")) {
        return nums as number[];
      }
    }
  }

  return null;
}

function avg(nums: Array<number | null | undefined>): number | null {
  const xs = nums.filter((x): x is number => typeof x === "number");
  if (!xs.length) return null;
  return xs.reduce((a, b) => a + b, 0) / xs.length;
}

function getTeamAdvantageFromOverlay(
  ov: ShotNumberOverlay | null | undefined,
  team: Team01,
): number | null {
  const arr = getAdvantageScaleArray(ov);
  if (!arr) return null;

  if (team === 0) return avg([arr[0], arr[1]]);
  return avg([arr[2], arr[3]]);
}

function getOpponentAdvantageFromOverlay(
  ov: ShotNumberOverlay | null | undefined,
  team: Team01,
): number | null {
  return getTeamAdvantageFromOverlay(ov, otherTeam(team));
}

function getAdvantageDeltaForHitterTeam(
  prevOv: ShotNumberOverlay | null,
  ov: ShotNumberOverlay | null,
  hitterTeam: Team01 | null,
): number | null {
  if (hitterTeam == null) return null;

  const before = getTeamAdvantageFromOverlay(prevOv, hitterTeam);
  const after = getTeamAdvantageFromOverlay(ov, hitterTeam);

  if (before == null || after == null) return null;
  return round2(after - before);
}

function getAdvantageDeltaAgainstHitterTeam(
  prevOv: ShotNumberOverlay | null,
  ov: ShotNumberOverlay | null,
  hitterTeam: Team01 | null,
): number | null {
  if (hitterTeam == null) return null;

  const before = getOpponentAdvantageFromOverlay(prevOv, hitterTeam);
  const after = getOpponentAdvantageFromOverlay(ov, hitterTeam);

  if (before == null || after == null) return null;
  return round2(after - before);
}

function isFinalOverlay(ov: ShotNumberOverlay | null | undefined): boolean {
  return (ov as any)?.rawShot?.is_final === true;
}

function getOverlayPlayerId(
  ov: ShotNumberOverlay | null | undefined,
): PlayerId | null {
  const pid = (ov as any)?.playerId ?? (ov as any)?.rawShot?.player_id;
  return isPlayerId(pid) ? pid : null;
}

function getPounceRowsForRallyShot(args: {
  pounceWindowRows: PounceWindowRow[];
  rallyIndex: number;
  shotNumber: number;
}): PounceWindowRow[] {
  const { pounceWindowRows, rallyIndex, shotNumber } = args;

  return pounceWindowRows.filter((p: any) => {
    const ri = (p as any)?.rallyIndex;
    const si = (p as any)?.shotIndex;
    return ri === rallyIndex && (si === shotNumber || si === shotNumber - 1);
  });
}

function pounceWasTaken(p: any): boolean {
  return p?.pounceResult?.taken === true;
}

function pounceWasMissed(p: any): boolean {
  return p?.pounceResult?.taken === false;
}

function pounceCreatedAttackWindow(p: any): boolean {
  return (
    p?.attackBand?.entered === true ||
    p?.attackBand?.firstAttackablePoint != null ||
    p?.attackBand?.bestAttackPoint != null
  );
}

function buildLeverageTags(args: {
  ov: ShotNumberOverlay | null;
  isFinalShot: boolean;
  popupCreated: boolean;
  popupExploited: boolean;
  createdAttackWindow: boolean;
  tookPounce: boolean;
  missedPounce: boolean;
  advantageDeltaAgainstHitterTeam: number | null;
}): string[] {
  const {
    ov,
    isFinalShot,
    popupCreated,
    popupExploited,
    createdAttackWindow,
    tookPounce,
    missedPounce,
    advantageDeltaAgainstHitterTeam,
  } = args;

  const out: string[] = [];
  const kinds = storyKindSet(ov);

  if (popupCreated) out.push("POPUP CREATED");
  if (popupExploited) out.push("POPUP EXPLOITED");
  if (createdAttackWindow) out.push("ATTACK WINDOW");
  if (tookPounce) out.push("POUNCE TAKEN");
  if (missedPounce) out.push("POUNCE MISSED");

  if (kinds.has("forced_fault") || kinds.has("winner_type:forced_fault")) {
    out.push("FORCED FAULT");
  }
  if (kinds.has("unforced_error")) out.push("UNFORCED ERROR");
  if (kinds.has("fault_net")) out.push("NET");
  if (kinds.has("fault_out")) out.push("OUT");

  if (
    advantageDeltaAgainstHitterTeam != null &&
    advantageDeltaAgainstHitterTeam >= 0.12
  ) {
    out.push("LEVERAGE DROP");
  }

  if (isFinalShot) out.push("FINISH");

  return out;
}

function buildExplanationShort(args: {
  ov: ShotNumberOverlay | null;
  phaseLabel: string;
  evidence: RallyCausalityEvidence[];
  isFinalShot: boolean;
  popupCreated: boolean;
  popupExploited: boolean;
  createdAttackWindow: boolean;
  tookPounce: boolean;
  missedPounce: boolean;
  advantageDeltaAgainstHitterTeam: number | null;
}): string {
  const {
    ov,
    phaseLabel,
    evidence,
    isFinalShot,
    popupCreated,
    popupExploited,
    createdAttackWindow,
    tookPounce,
    missedPounce,
    advantageDeltaAgainstHitterTeam,
  } = args;

  const kinds = storyKindSet(ov);
  const evidenceSummary = summarizeEvidence(evidence);

  if (evidenceSummary) {
    return `${phaseLabel}: ${evidenceSummary}.`;
  }

  if (popupCreated) {
    return "This shot created an attackable ball that likely set up an attack.";
  }
  if (popupExploited) {
    return "This shot exploited an attackable popup.";
  }
  if (createdAttackWindow && tookPounce) {
    return "Attack window was created and taken.";
  }
  if (createdAttackWindow && missedPounce) {
    return "Attack window appeared but was not taken.";
  }
  if (kinds.has("forced_fault") || kinds.has("winner_type:forced_fault")) {
    return "Pressure here appears to have forced the next fault.";
  }
  if (kinds.has("unforced_error"))
    return "This looks like a direct error point.";
  if (kinds.has("fault_net")) return "This shot finished in the net.";
  if (kinds.has("fault_out")) return "This shot finished out.";
  if (
    advantageDeltaAgainstHitterTeam != null &&
    advantageDeltaAgainstHitterTeam >= 0.16
  ) {
    return "Positional leverage swung sharply against the hitter's team.";
  }
  if (
    advantageDeltaAgainstHitterTeam != null &&
    advantageDeltaAgainstHitterTeam >= 0.08
  ) {
    return "This shot moved the rally against the hitter's team.";
  }
  if (isFinalShot) return "Terminal finish shot.";
  return "Moderate structural influence on the rally.";
}

function buildExplanationLong(args: {
  shotNumber: number;
  hitterTeam: Team01 | null;
  advantageDeltaAgainstHitterTeam: number | null;
  popupCreated: boolean;
  popupExploited: boolean;
  createdAttackWindow: boolean;
  tookPounce: boolean;
  missedPounce: boolean;
  isFinalShot: boolean;
}): string {
  const {
    shotNumber,
    hitterTeam,
    advantageDeltaAgainstHitterTeam,
    popupCreated,
    popupExploited,
    createdAttackWindow,
    tookPounce,
    missedPounce,
    isFinalShot,
  } = args;

  const parts: string[] = [];

  parts.push(`Shot ${shotNumber}.`);

  if (hitterTeam != null) {
    parts.push(`Hitter was on team ${hitterTeam}.`);
  }

  if (popupCreated) {
    parts.push("This shot was marked as creating a popup.");
  }

  if (popupExploited) {
    parts.push("A popup was exploited on this shot.");
  }

  if (createdAttackWindow) {
    parts.push("An attackable window appears to have opened around this shot.");
  }

  if (tookPounce) {
    parts.push("That attackable window appears to have been taken.");
  } else if (missedPounce) {
    parts.push("That attackable window appears not to have been taken.");
  }

  if (advantageDeltaAgainstHitterTeam != null) {
    if (advantageDeltaAgainstHitterTeam > 0) {
      parts.push(
        `Opponent-side leverage increased by about ${round2(
          advantageDeltaAgainstHitterTeam,
        )}.`,
      );
    } else if (advantageDeltaAgainstHitterTeam < 0) {
      parts.push(
        `Opponent-side leverage decreased by about ${round2(
          Math.abs(advantageDeltaAgainstHitterTeam),
        )}.`,
      );
    }
  }

  if (isFinalShot) {
    parts.push("This was the terminal shot of the rally.");
  }

  return parts.join(" ");
}

function terminalOutcomeLabel(ov: ShotNumberOverlay | null): string | null {
  const kinds = storyKindSet(ov);
  if (kinds.has("fault_net")) return "Net fault";
  if (kinds.has("fault_out")) return "Out fault";
  if (kinds.has("unforced_error")) return "Unforced error";
  if (kinds.has("forced_fault") || kinds.has("winner_type:forced_fault")) {
    return "Forced fault";
  }
  if (isFinalOverlay(ov)) return "Terminal shot";
  return null;
}

function buildCausalityEvidence(args: {
  ov: ShotNumberOverlay | null;
  shotNumber: number;
  phaseLabel: string;
  quality: number | null;
  popupCreated: boolean;
  popupExploited: boolean;
  createdAttackWindow: boolean;
  tookPounce: boolean;
  missedPounce: boolean;
  advantageDeltaAgainstHitterTeam: number | null;
  isFinalShot: boolean;
  cvSignal: CvAttackableBallSignal | null;
}): RallyCausalityEvidence[] {
  const {
    ov,
    shotNumber,
    phaseLabel,
    quality,
    popupCreated,
    popupExploited,
    createdAttackWindow,
    tookPounce,
    missedPounce,
    advantageDeltaAgainstHitterTeam,
    isFinalShot,
    cvSignal,
  } = args;

  const out: RallyCausalityEvidence[] = [];

  if (
    shotNumber <= 5 ||
    phaseLabel === "Dink pattern" ||
    phaseLabel === "Reset"
  ) {
    addEvidence(out, {
      kind: "shot_phase",
      label: phaseLabel,
      detail: `This was a ${phaseLabel.toLowerCase()} moment.`,
      weight: shotNumber <= 5 ? 0.35 : 0.2,
      source: "derived",
      polarity: "neutral",
    });
  }

  if (typeof quality === "number") {
    if (quality <= 0.25) {
      addEvidence(out, {
        kind: "shot_quality",
        label: "Low quality",
        detail: `PB Vision quality was ${quality.toFixed(2)}, which is a strong warning signal.`,
        weight: 1.05,
        source: "insights",
        polarity: "blame",
      });
    } else if (quality <= 0.4) {
      addEvidence(out, {
        kind: "shot_quality",
        label: "Suboptimal quality",
        detail: `PB Vision quality was ${quality.toFixed(2)}, enough to treat this as a possible pressure source.`,
        weight: 0.55,
        source: "insights",
        polarity: "blame",
      });
    } else if (quality >= 0.78) {
      addEvidence(out, {
        kind: "shot_quality",
        label: "High quality",
        detail: `PB Vision quality was ${quality.toFixed(2)}, supporting credit for this shot.`,
        weight: 0.45,
        source: "insights",
        polarity: "credit",
      });
    }
  }

  if (popupCreated) {
    const fromCv = cvSignal?.likelyAttackableBallCreated === true;
    addEvidence(out, {
      kind: "ball_attackability",
      label: "Attackable ball",
      detail: fromCv
        ? `CV/ball shape suggests this ball became attackable (${cvSignal.reasons.join(", ")}).`
        : "PB Vision marked this shot as creating a popup.",
      weight: fromCv ? 1.35 : 1.55,
      source: fromCv ? "cv" : "insights",
      polarity: "blame",
    });
  }

  if (popupExploited) {
    addEvidence(out, {
      kind: "ball_attackability",
      label: "Popup exploited",
      detail: "The receiving side appears to have converted an attackable popup.",
      weight: 1.0,
      source: "derived",
      polarity: "credit",
    });
  }

  if (createdAttackWindow) {
    addEvidence(out, {
      kind: "pounce_window",
      label: tookPounce ? "Attack window taken" : "Attack window",
      detail: tookPounce
        ? "The pounce-window model found an attack window that was taken."
        : "The pounce-window model found an attackable opportunity.",
      weight: tookPounce ? 1.0 : 0.75,
      source: "pounce",
      polarity: tookPounce ? "credit" : "neutral",
    });
  }

  if (missedPounce) {
    addEvidence(out, {
      kind: "missed_attack",
      label: "Missed chance",
      detail: "An attack window appeared but the player did not take it.",
      weight: 0.85,
      source: "pounce",
      polarity: "blame",
    });
  }

  if (advantageDeltaAgainstHitterTeam != null) {
    if (advantageDeltaAgainstHitterTeam >= 0.16) {
      addEvidence(out, {
        kind: "advantage_shift",
        label: "Major leverage drop",
        detail: `Opponent-side advantage rose by ${advantageDeltaAgainstHitterTeam.toFixed(2)} after this shot.`,
        weight: 1.15,
        source: "insights",
        polarity: "blame",
      });
    } else if (advantageDeltaAgainstHitterTeam >= 0.08) {
      addEvidence(out, {
        kind: "advantage_shift",
        label: "Leverage drop",
        detail: `Opponent-side advantage rose by ${advantageDeltaAgainstHitterTeam.toFixed(2)} after this shot.`,
        weight: 0.65,
        source: "insights",
        polarity: "blame",
      });
    } else if (advantageDeltaAgainstHitterTeam <= -0.12) {
      addEvidence(out, {
        kind: "advantage_shift",
        label: "Leverage gained",
        detail: `Opponent-side advantage fell by ${Math.abs(advantageDeltaAgainstHitterTeam).toFixed(2)} after this shot.`,
        weight: 0.65,
        source: "insights",
        polarity: "credit",
      });
    }
  }

  const terminal = terminalOutcomeLabel(ov);
  if (terminal) {
    addEvidence(out, {
      kind: "terminal_outcome",
      label: terminal,
      detail: isFinalShot
        ? "This shot ended the rally."
        : "This shot has an explicit terminal/error marker.",
      weight: isFinalShot ? 0.9 : 0.65,
      source: "insights",
      polarity:
        terminal === "Forced fault" || terminal === "Terminal shot"
          ? "credit"
          : "blame",
    });
  }

  return out.sort((a, b) => b.weight - a.weight);
}

function evidenceScore(
  evidence: RallyCausalityEvidence[],
  polarity?: CausalityEvidencePolarity,
): number {
  return round2(
    evidence
      .filter((e) => (polarity ? e.polarity === polarity : true))
      .reduce((sum, e) => sum + Math.max(0, e.weight), 0),
  );
}

function summarizeEvidence(evidence: RallyCausalityEvidence[]): string {
  const top = evidence.slice(0, 2);
  if (!top.length) return "";
  return top.map((e) => e.label.toLowerCase()).join(" + ");
}

function computeInevitabilityScore(args: {
  popupCreated: boolean;
  popupExploited: boolean;
  createdAttackWindow: boolean;
  tookPounce: boolean;
  missedPounce: boolean;
  isFinalShot: boolean;
  advantageDeltaAgainstHitterTeam: number | null;
  quality: number | null;
  ov: ShotNumberOverlay | null;
}): number {
  const {
    popupCreated,
    popupExploited,
    createdAttackWindow,
    tookPounce,
    missedPounce,
    isFinalShot,
    advantageDeltaAgainstHitterTeam,
    quality,
    ov,
  } = args;

  const kinds = storyKindSet(ov);
  let score = 0;

  if (popupCreated) score += 3.2;
  if (popupExploited) score += 1.8;
  if (createdAttackWindow) score += 1.6;
  if (tookPounce) score += 1.4;
  if (missedPounce) score += 0.7;

  if (kinds.has("forced_fault") || kinds.has("winner_type:forced_fault")) {
    score += 1.7;
  }
  if (kinds.has("unforced_error")) score += 1.2;
  if (kinds.has("fault_net")) score += 1.1;
  if (kinds.has("fault_out")) score += 1.1;

  if (advantageDeltaAgainstHitterTeam != null) {
    score += clamp(advantageDeltaAgainstHitterTeam * 10, -1.0, 2.8);
  }

  if (typeof quality === "number") {
    if (quality <= 0.2) score += 0.7;
    else if (quality <= 0.35) score += 0.35;
  }

  if (isFinalShot) score += 0.9;

  return round2(score);
}
function computeSwingScore(args: {
  advantageDeltaForHitterTeam: number | null;
  advantageDeltaAgainstHitterTeam: number | null;
}): number {
  const { advantageDeltaForHitterTeam, advantageDeltaAgainstHitterTeam } = args;

  if (advantageDeltaAgainstHitterTeam != null) {
    return round2(Math.abs(advantageDeltaAgainstHitterTeam));
  }

  if (advantageDeltaForHitterTeam != null) {
    return round2(Math.abs(advantageDeltaForHitterTeam));
  }

  return 0;
}

function computeConfidence(args: {
  popupCreated: boolean;
  popupExploited: boolean;
  createdAttackWindow: boolean;
  tookPounce: boolean;
  missedPounce: boolean;
  advantageDeltaAgainstHitterTeam: number | null;
  ov: ShotNumberOverlay | null;
}): number {
  const {
    popupCreated,
    popupExploited,
    createdAttackWindow,
    tookPounce,
    missedPounce,
    advantageDeltaAgainstHitterTeam,
    ov,
  } = args;

  let c = 0.2;

  if (popupCreated) c += 0.22;
  if (popupExploited) c += 0.18;
  if (createdAttackWindow) c += 0.2;
  if (tookPounce || missedPounce) c += 0.1;

  if (
    advantageDeltaAgainstHitterTeam != null &&
    Math.abs(advantageDeltaAgainstHitterTeam) >= 0.08
  ) {
    c += 0.18;
  }

  const kinds = storyKindSet(ov);
  if (
    kinds.has("forced_fault") ||
    kinds.has("winner_type:forced_fault") ||
    kinds.has("unforced_error") ||
    kinds.has("fault_net") ||
    kinds.has("fault_out")
  ) {
    c += 0.12;
  }

  return round2(clamp(c, 0, 1));
}
function chooseNoReturnShotNumber(
  shotRows: RallyCausalityShotRow[],
): number | null {
  if (!shotRows.length) return null;

  for (let i = 0; i < shotRows.length - 1; i++) {
    const cur = shotRows[i];
    const next = shotRows[i + 1];

    if (
      cur.popupCreated &&
      (next.popupExploited ||
        next.createdAttackWindow ||
        next.leverageTags.includes("FORCED FAULT") ||
        next.isFinalShot)
    ) {
      return cur.shotNumber;
    }
  }

  for (let i = 0; i < shotRows.length; i++) {
    const cur = shotRows[i];
    if (cur.causalScore < 2.4) continue;

    const laterMax = shotRows
      .slice(i + 1)
      .reduce((mx, r) => Math.max(mx, r.causalScore), 0);

    if (laterMax <= cur.causalScore + 0.4) {
      return cur.shotNumber;
    }
  }

  return null;
}

function chooseSetupShotNumber(
  shotRows: RallyCausalityShotRow[],
  noReturnShotNumber: number | null,
): number | null {
  const candidates = shotRows.filter((r) => {
    if (noReturnShotNumber != null && r.shotNumber >= noReturnShotNumber) {
      return false;
    }

    return (
      r.popupCreated ||
      r.popupExploited ||
      r.createdAttackWindow ||
      r.missedPounce ||
      r.causalScore >= 1.8
    );
  });

  if (!candidates.length) return null;

  candidates.sort((a, b) => {
    if (b.causalScore !== a.causalScore) {
      return b.causalScore - a.causalScore;
    }
    return a.shotNumber - b.shotNumber;
  });

  return candidates[0].shotNumber;
}

function chooseLargestSwingShotNumber(
  shotRows: RallyCausalityShotRow[],
): number | null {
  if (!shotRows.length) return null;

  let best: RallyCausalityShotRow | null = null;

  for (const r of shotRows) {
    if (!best || r.swingScore > best.swingScore) {
      best = r;
    }
  }

  return best?.shotNumber ?? null;
}

function chooseFinishShotNumber(
  shotRows: RallyCausalityShotRow[],
): number | null {
  const finals = shotRows.filter((r) => r.isFinalShot);
  if (finals.length) return finals[finals.length - 1].shotNumber;
  return shotRows.length ? shotRows[shotRows.length - 1].shotNumber : null;
}

function summarizeRallyNarrative(args: {
  shotRows: RallyCausalityShotRow[];
  setupShotNumber: number | null;
  noReturnShotNumber: number | null;
  finishShotNumber: number | null;
  decisiveTeam: Team01 | null;
  decisivePlayerId: PlayerId | null;
}): { short: string; long: string } {
  const {
    shotRows,
    setupShotNumber,
    noReturnShotNumber,
    finishShotNumber,
    decisiveTeam,
    decisivePlayerId,
  } = args;

  const shortParts: string[] = [];
  const longParts: string[] = [];
  const byShotNumber = new Map(shotRows.map((r) => [r.shotNumber, r]));

  if (setupShotNumber != null) shortParts.push(`Root: ${setupShotNumber}`);
  if (noReturnShotNumber != null)
    shortParts.push(`No return: ${noReturnShotNumber}`);
  if (finishShotNumber != null) shortParts.push(`Finish: ${finishShotNumber}`);

  if (setupShotNumber != null) {
    const row = byShotNumber.get(setupShotNumber);
    longParts.push(
      `Shot ${setupShotNumber} looks like the root pressure point${row?.explanationShort ? `: ${row.explanationShort}` : "."}`,
    );
  }
  if (noReturnShotNumber != null) {
    const row = byShotNumber.get(noReturnShotNumber);
    longParts.push(
      `Shot ${noReturnShotNumber} looks like the point-of-no-return${row?.explanationShort ? `: ${row.explanationShort}` : "."}`,
    );
  }
  if (finishShotNumber != null) {
    const row = byShotNumber.get(finishShotNumber);
    longParts.push(
      `Shot ${finishShotNumber} looks like the finish${row?.explanationShort ? `: ${row.explanationShort}` : "."}`,
    );
  }
  if (decisivePlayerId != null) {
    longParts.push(`Most decisive player: ${decisivePlayerId}.`);
  } else if (decisiveTeam != null) {
    longParts.push(`Most decisive team: ${decisiveTeam}.`);
  }

  return {
    short: shortParts.length
      ? shortParts.join(" • ")
      : "No strong causality markers yet.",
    long: longParts.length
      ? longParts.join(" ")
      : "No strong causality story detected for this rally yet.",
  };
}

function assignOverlayKinds(args: {
  shotRows: RallyCausalityShotRow[];
  setupShotNumber: number | null;
  noReturnShotNumber: number | null;
  finishShotNumber: number | null;
}): RallyCausalityShotRow[] {
  const { shotRows, setupShotNumber, noReturnShotNumber, finishShotNumber } =
    args;

  return shotRows.map((r) => {
    let overlayKind: CausalityOverlayKind | null = null;

    if (finishShotNumber != null && r.shotNumber === finishShotNumber) {
      overlayKind = "finish";
    }
    if (setupShotNumber != null && r.shotNumber === setupShotNumber) {
      overlayKind = "setup";
    }
    if (noReturnShotNumber != null && r.shotNumber === noReturnShotNumber) {
      overlayKind = "no_return";
    }

    if (
      overlayKind == null &&
      (r.popupCreated ||
        r.popupExploited ||
        r.createdAttackWindow ||
        r.swingScore >= 0.16 ||
        r.causalScore >= 2.0)
    ) {
      overlayKind = "shift";
    }

    if (
      overlayKind == null &&
      (r.missedPounce ||
        r.leverageTags.includes("UNFORCED ERROR") ||
        evidenceScore(r.evidence, "blame") >= 1.0)
    ) {
      overlayKind = "warning";
    }

    return {
      ...r,
      overlayKind,
    };
  });
}

function buildShotRow(args: {
  row: KitchenArrivalRow;
  shotNumber: number;
  prevOv: ShotNumberOverlay | null;
  ov: ShotNumberOverlay | null;
  pounceWindowRows: PounceWindowRow[];
  cvJson: CvJson | null;
}): RallyCausalityShotRow {
  const { row, shotNumber, prevOv, ov, pounceWindowRows, cvJson } = args;

  const cvSignal = deriveCvAttackableBallSignal({
    cvJson,
    rallyIndex: row.rallyIndex,
    shotNumber,
  });

  const attackableBallCreatedFromCv =
    cvSignal?.likelyAttackableBallCreated === true;

  const hitterPlayerId = getOverlayPlayerId(ov);
  const hitterTeam = teamOfPlayerId(hitterPlayerId);

  const shotType = getShotType(ov);
  const quality = getQuality(ov);
  const isFinalShot = isFinalOverlay(ov);
  const phaseLabel = getPhaseLabel({ shotNumber, shotType, isFinalShot });

  const popupCreated = getPopupCreated(ov) || attackableBallCreatedFromCv;

  const popupExploited =
    (ov as any)?.popupExploited === true ||
    hasAnyKind(ov, ["popup_exploited", "popup:exploited"]);

  const pounceRows = getPounceRowsForRallyShot({
    pounceWindowRows,
    rallyIndex: row.rallyIndex,
    shotNumber,
  });

  const createdAttackWindow = pounceRows.some((p) =>
    pounceCreatedAttackWindow(p),
  );
  const tookPounce = pounceRows.some((p) => pounceWasTaken(p));
  const missedPounce = pounceRows.some((p) => pounceWasMissed(p));

  const advantageBefore =
    hitterTeam != null ? getTeamAdvantageFromOverlay(prevOv, hitterTeam) : null;
  const advantageAfter =
    hitterTeam != null ? getTeamAdvantageFromOverlay(ov, hitterTeam) : null;

  const advantageDeltaForHitterTeam = getAdvantageDeltaForHitterTeam(
    prevOv,
    ov,
    hitterTeam,
  );

  const advantageDeltaAgainstHitterTeam = getAdvantageDeltaAgainstHitterTeam(
    prevOv,
    ov,
    hitterTeam,
  );

  const evidence = buildCausalityEvidence({
    ov,
    shotNumber,
    phaseLabel,
    quality,
    popupCreated,
    popupExploited,
    createdAttackWindow,
    tookPounce,
    missedPounce,
    advantageDeltaAgainstHitterTeam,
    isFinalShot,
    cvSignal,
  });

  const leverageTags = buildLeverageTags({
    ov,
    isFinalShot,
    popupCreated,
    popupExploited,
    createdAttackWindow,
    tookPounce,
    missedPounce,
    advantageDeltaAgainstHitterTeam,
  });

  const explanationShort = buildExplanationShort({
    ov,
    phaseLabel,
    evidence,
    isFinalShot,
    popupCreated,
    popupExploited,
    createdAttackWindow,
    tookPounce,
    missedPounce,
    advantageDeltaAgainstHitterTeam,
  });

  const explanationLong = buildExplanationLong({
    shotNumber,
    hitterTeam,
    advantageDeltaAgainstHitterTeam,
    popupCreated,
    popupExploited,
    createdAttackWindow,
    tookPounce,
    missedPounce,
    isFinalShot,
  });

  const inevitabilityScore = computeInevitabilityScore({
    popupCreated,
    popupExploited,
    createdAttackWindow,
    tookPounce,
    missedPounce,
    isFinalShot,
    advantageDeltaAgainstHitterTeam,
    quality,
    ov,
  });

  const swingScore = computeSwingScore({
    advantageDeltaForHitterTeam,
    advantageDeltaAgainstHitterTeam,
  });

  const confidence = computeConfidence({
    popupCreated,
    popupExploited,
    createdAttackWindow,
    tookPounce,
    missedPounce,
    advantageDeltaAgainstHitterTeam,
    ov,
  });

  const causalScore = round2(
    inevitabilityScore +
      evidenceScore(evidence) * 0.55 +
      evidenceScore(evidence, "blame") * 0.25 +
      evidenceScore(evidence, "credit") * 0.15,
  );

  return {
    rallyIndex: row.rallyIndex,
    shotNumber,
    hitterPlayerId,
    hitterTeam,

    shotType,
    quality,
    isFinalShot,

    storyKinds: Array.from(storyKindSet(ov)),
    popupCreated,
    popupExploited,
    createdAttackWindow,
    tookPounce,
    missedPounce,

    advantageBefore: advantageBefore == null ? null : round2(advantageBefore),
    advantageAfter: advantageAfter == null ? null : round2(advantageAfter),
    advantageDeltaForHitterTeam,
    advantageDeltaAgainstHitterTeam,

    inevitabilityScore,
    swingScore,
    confidence,
    causalScore,
    phaseLabel,

    leverageTags,
    evidence,
    explanationShort,
    explanationLong,
    overlayKind: null,
  };
}

export function buildRallyCausalitySummaries(args: {
  rows: KitchenArrivalRow[];
  pounceWindowRows?: PounceWindowRow[];
    cvJson: CvJson | null;

}): RallyCausalitySummary[] {
  const { rows, pounceWindowRows = [], cvJson } = args;

  return rows.map((row) => {
    const numShots = getNumShots(row);
    const overlays = getOverlays(row);

    const shotRowsBase: RallyCausalityShotRow[] = [];

    for (let shotNumber = 1; shotNumber <= numShots; shotNumber++) {
      const ov = findOverlayByShotNumber(row, shotNumber);
      if (!ov) continue;

      const prevOv =
        shotNumber > 1 ? findOverlayByShotNumber(row, shotNumber - 1) : null;

      shotRowsBase.push(
        buildShotRow({
          row,
          shotNumber,
          prevOv,
          ov,
          pounceWindowRows,
          cvJson,
        }),
      );
    }

    const largestSwingShotNumber = chooseLargestSwingShotNumber(shotRowsBase);
    const finishShotNumber = chooseFinishShotNumber(shotRowsBase);
    const noReturnShotNumber = chooseNoReturnShotNumber(shotRowsBase);
    const setupShotNumber = chooseSetupShotNumber(
      shotRowsBase,
      noReturnShotNumber,
    );

    const shotRows = assignOverlayKinds({
      shotRows: shotRowsBase,
      setupShotNumber,
      noReturnShotNumber,
      finishShotNumber,
    });

    const decisiveRow =
      shotRows.find((r) => r.shotNumber === noReturnShotNumber) ??
      shotRows.find((r) => r.shotNumber === setupShotNumber) ??
      shotRows.find((r) => r.shotNumber === finishShotNumber) ??
      [...shotRows].sort(
        (a, b) => b.causalScore - a.causalScore,
      )[0] ??
      null;

    const decisivePlayerId = decisiveRow?.hitterPlayerId ?? null;
    const decisiveTeam = decisiveRow?.hitterTeam ?? null;

    const narrative = summarizeRallyNarrative({
      shotRows,
      setupShotNumber,
      noReturnShotNumber,
      finishShotNumber,
      decisiveTeam,
      decisivePlayerId,
    });

    return {
      rallyIndex: row.rallyIndex,
      numShots,
      winningTeam: getWinnerTeam(row),
      pointAwarded: getPointAwarded(row),

      shotRows,

      largestSwingShotNumber,
      setupShotNumber,
      noReturnShotNumber,
      finishShotNumber,

      decisivePlayerId,
      decisiveTeam,

      summaryShort: narrative.short,
      summaryLong: narrative.long,
    };
  });
}

export function buildRallyCausalitySummaryMap(args: {
  rows: KitchenArrivalRow[];
  pounceWindowRows?: PounceWindowRow[];
    cvJson: CvJson | null;

}): Map<number, RallyCausalitySummary> {
  const summaries = buildRallyCausalitySummaries(args);
  return new Map(summaries.map((s) => [s.rallyIndex, s]));
}

export function getCausalityOverlayText(args: {
  summary: RallyCausalitySummary | null | undefined;
  shotNumber: number | null | undefined;
}): string | undefined {
  const { summary, shotNumber } = args;
  if (!summary || shotNumber == null) return undefined;

  const row = summary.shotRows.find((r) => r.shotNumber === shotNumber);
  if (!row) return undefined;

  const prefix =
    row.overlayKind === "setup"
      ? "SETUP"
      : row.overlayKind === "shift"
        ? "SHIFT"
        : row.overlayKind === "no_return"
          ? "NO RETURN"
          : row.overlayKind === "finish"
            ? "FINISH"
            : row.overlayKind === "warning"
              ? "WARNING"
              : "CAUSE";

  return `${prefix} • ${row.explanationShort}`;
}



export type CvAttackableBallSignal = {
  likelyAttackableBallCreated: boolean;
  score: number;
  reasons: string[];
  peakHeight: number | null;
  heightOverNet: number | null;
  hitterCourtY: number | null;
  bestOpponentCourtY: number | null;
};



function getRallyFrames(
  cvJson: CvJson | null | undefined,
  rallyIndex: number | null | undefined,
): CvFrame[] | null {
  if (!cvJson || rallyIndex == null || rallyIndex < 0) return null;
  const session = cvJson.sessions?.[0];
  const rally = session?.rallies?.[rallyIndex];
  const frames = rally?.frames;
  return Array.isArray(frames) && frames.length > 0 ? frames : null;
}

function getShotFramesForPlayerSwitch(frames: CvFrame[]): Array<{
  frameIndex: number;
  shot: CvShotBallEvent;
}> {
  const out: Array<{ frameIndex: number; shot: CvShotBallEvent }> = [];

  for (let i = 0; i < frames.length; i++) {
    const shot = frames[i]?.balls?.shot;
    if (!shot) continue;
    if (typeof shot.player !== "number") continue;
    out.push({ frameIndex: i, shot });
  }

  return out;
}

function getBestOpponentCourtY(args: {
  positions: Array<{ x: number; y: number } | undefined> | undefined;
  hitterTeam: Team01;
}): number | null {
  const { positions, hitterTeam } = args;
  if (!positions || positions.length < 4) return null;

  const opponentIds = hitterTeam === 0 ? [2, 3] : [0, 1];
  const ys = opponentIds
    .map((pid) => positions[pid]?.y)
    .filter((y): y is number => typeof y === "number" && Number.isFinite(y));

  if (!ys.length) return null;

  // "best" here means most attack-ready / most advanced.
  // You may later want different logic by side/orientation.
  return hitterTeam === 0 ? Math.min(...ys) : Math.max(...ys);
}

function getHitterCourtY(args: {
  positions: Array<{ x: number; y: number } | undefined> | undefined;
  hitterPlayerId: number;
}): number | null {
  const p = args.positions?.[args.hitterPlayerId];
  return p && typeof p.y === "number" ? p.y : null;
}

export function deriveCvAttackableBallSignal(args: {
  cvJson: CvJson | null | undefined;
  rallyIndex: number | null | undefined;
  shotNumber: number;
}): CvAttackableBallSignal | null {
  const { cvJson, rallyIndex, shotNumber } = args;

  const frames = getRallyFrames(cvJson, rallyIndex);
  if (!frames) return null;

  const shotFrames = getShotFramesForPlayerSwitch(frames);
  if (!shotFrames.length) return null;

  // Assumes shotNumber is 1-based and aligned to detected shot events.
  const cur = shotFrames[shotNumber - 1];
  if (!cur) return null;

  const next = shotFrames[shotNumber];
  const shot = cur.shot;
  const hitterPlayerId = shot.player;
  const hitterTeam = teamOfPlayerId(hitterPlayerId);

  if (hitterTeam == null) return null;

  const nextHitterTeam =
    next && typeof next.shot.player === "number"
      ? teamOfPlayerId(next.shot.player)
      : null;

  const positions = frames[cur.frameIndex]?.player_court_positions;
  const hitterCourtY = getHitterCourtY({
    positions,
    hitterPlayerId,
  });
  const bestOpponentCourtY = getBestOpponentCourtY({
    positions,
    hitterTeam,
  });

  const heightOverNet =
    typeof shot.ball_height_over_net === "number"
      ? shot.ball_height_over_net
      : null;

  const peakHeight =
    typeof shot.ball_peak?.z === "number"
      ? shot.ball_peak.z
      : null;

  const reasons: string[] = [];
  let score = 0;

  if (heightOverNet != null && heightOverNet >= 3.2) {
    score += 1.2;
    reasons.push("high_over_net");
  }

  if (peakHeight != null && peakHeight >= 4.0) {
    score += 1.0;
    reasons.push("high_peak");
  }

  if (nextHitterTeam != null && nextHitterTeam === otherTeam(hitterTeam)) {
    score += 0.8;
    reasons.push("opponent_next_shot");
  }

  // These Y thresholds are intentionally starter heuristics.
  // Tune once you inspect a few examples.
  if (hitterCourtY != null) {
    if (hitterTeam === 1 && hitterCourtY >= 28) {
      score += 0.5;
      reasons.push("hitter_deep");
    }
    if (hitterTeam === 0 && hitterCourtY <= 16) {
      score += 0.5;
      reasons.push("hitter_deep");
    }
  }

  if (bestOpponentCourtY != null) {
    if (hitterTeam === 1 && bestOpponentCourtY <= 10.5) {
      score += 0.9;
      reasons.push("opponent_forward");
    }
    if (hitterTeam === 0 && bestOpponentCourtY >= 33.5) {
      score += 0.9;
      reasons.push("opponent_forward");
    }
  }

  const likelyAttackableBallCreated = score >= 2.4;

  return {
    likelyAttackableBallCreated,
    score: round2(score),
    reasons,
    peakHeight,
    heightOverNet,
    hitterCourtY,
    bestOpponentCourtY,
  };
}
